import { useState, useCallback } from 'react';
import { useSpeechRecognition } from './useSpeechRecognition';
import type {
  UseSpeechRecognitionProps,
  UseSpeechRecognitionReturn,
  SpeechRecognitionEvent,
} from './types';

// Wrapper hook - handles text state and formatting
interface UseSpeechToTextProps extends Omit<UseSpeechRecognitionProps, 'onResult'> {
  onResult?: (text: string) => void;
}

interface UseSpeechToTextReturn extends Omit<UseSpeechRecognitionReturn, 'status'> {
  text: string;
  resetText: () => void;
}

export const useSpeechToText = ({
  onResult,
  ...props
}: UseSpeechToTextProps = {}): UseSpeechToTextReturn => {
  const [text, setText] = useState<string>('');

  const handleResult = useCallback(
    (event: SpeechRecognitionEvent) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join(' ');

      setText(transcript);
      onResult?.(transcript);
    },
    [onResult],
  );

  const { status, ...recognition } = useSpeechRecognition({
    ...props,
    onResult: handleResult,
  });

  const resetText = useCallback(() => {
    setText('');
  }, []);

  return {
    ...recognition,
    text,
    resetText,
  };
};
